import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Grid,
  Divider,
  IconButton,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  InputLabel,
  TablePagination,
  Tabs,
  Tab,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  PortfolioCreateAnnouncement,
  PortfolioListAnnouncement,
  PortfolioDeleteAnnouncement,
  PortfolioUpdateAnnouncement,
  PortfolioSortAnnouncement,
} from "../../util/API/portfolio";

const formatExpiryDateTime = (dateTimeString) => {
  if (!dateTimeString) return "";
  let formatted = dateTimeString.replace("T", " ");
  if (formatted.length === 16) {
    formatted += ":00";
  }
  return formatted;
};

const AdminAnnouncements = ({ funds, setSnackbar, setAlert, agentID }) => {
  const [isLoadingAnnouncements, setIsLoadingAnnouncements] = useState(false);
  const [isPublishingAnnouncement, setIsPublishingAnnouncement] = useState(false);
  const [isUpdatingAnnouncement, setIsUpdatingAnnouncement] = useState(false);
  const [deletingAnnouncementId, setDeletingAnnouncementId] = useState(null);

  const [announcements, setAnnouncements] = useState([]);
  const [activeTab, setActiveTab] = useState(0); // 0 = Active, 1 = Inactive

  // Pagination States
  const [pageActive, setPageActive] = useState(0);
  const [rowsPerPageActive, setRowsPerPageActive] = useState(5);

  const [pageInactive, setPageInactive] = useState(0);
  const [rowsPerPageInactive, setRowsPerPageInactive] = useState(5);

  const [announcementForm, setAnnouncementForm] = useState({
    title: "",
    body: "",
    audienceType: "all",
    targetFundId: "",
    expiryDate: "",
    status: true,
  });

  const [editAnnouncementDialog, setEditAnnouncementDialog] = useState(false);
  const [editAnnouncementForm, setEditAnnouncementForm] = useState({
    id: "",
    title: "",
    body: "",
    audienceType: "all",
    targetFundId: "",
    expiryDate: "",
    status: true,
    originalStatus: true,
    numberby: 0,
  });

  useEffect(() => {
    fetchAnnouncements();
  }, [funds]);

  const fetchAnnouncements = async () => {
    setIsLoadingAnnouncements(true);
    try {
      const response = await PortfolioListAnnouncement(
        "",
        "0",
        "",
        "",
        "",
        "0",
        "",
        ""
      );
      const records = response?.data?.body?.record;
      if (records) {
        const recordsArray = Array.isArray(records) ? records : [records];
        const mapped = recordsArray
          .map((item) => {
            const a = item.Announcements;
            if (!a) return null;
            const fundId = a.fund_id;
            const isAllInvestors = !fundId || fundId === "0" || fundId === 0;
            const targetFundName = isAllInvestors
              ? "All Investors"
              : funds.find((f) => String(f.id) === String(fundId))?.name || `Fund ID: ${fundId}`;

            return {
              id: a.id,
              title: a.title,
              body: a.content,
              audienceType: isAllInvestors ? "all" : "fund",
              targetFundId: fundId,
              targetFundName,
              expiryDate: a.expirydate || "",
              status: !(a.status === "inactive" || a.status === 0 || a.status === "0"),
              numberby: Number(a.numberby) || 0,
            };
          })
          .filter(Boolean)
          .sort((x, y) => Number(x.numberby) - Number(y.numberby) || Number(y.id) - Number(x.id));

        setAnnouncements(mapped);
      }
    } catch (error) {
      console.error("Failed to fetch announcements:", error);
    } finally {
      setIsLoadingAnnouncements(false);
    }
  };

  const handleAnnouncementSubmit = async () => {
    const { title, body, audienceType, targetFundId, expiryDate, status } = announcementForm;

    if (!title.trim() || !body.trim()) {
      setSnackbar({ open: true, message: "Title and body are required.", severity: "warning" });
      return;
    }
    if (audienceType === "fund" && !targetFundId) {
      setSnackbar({ open: true, message: "Please select a target fund.", severity: "warning" });
      return;
    }
    setIsPublishingAnnouncement(true);

    const formattedExpiry = formatExpiryDateTime(expiryDate);

    try {
      const response = await PortfolioCreateAnnouncement(
        agentID,
        audienceType === "fund" ? targetFundId : 0,
        title,
        body,
        formattedExpiry,
        status ? 1 : 0
      );

      const callStatus = response?.data?.header?.callStatus;
      if (callStatus === "1" || callStatus === 1) {
        await fetchAnnouncements();
        setAnnouncementForm({
          title: "",
          body: "",
          audienceType: "all",
          targetFundId: "",
          expiryDate: "",
          status: true,
        });
        setSnackbar({ open: true, message: response?.data?.header?.msg || "Announcement published.", severity: "success" });
      } else {
        setSnackbar({ open: true, message: response?.data?.header?.msg || "Failed to publish.", severity: "error" });
      }
    } catch (error) {
      console.error("Failed to create announcement:", error);
      setSnackbar({ open: true, message: "Something went wrong.", severity: "error" });
    } finally {
      setIsPublishingAnnouncement(false);
    }
  };

  const openEditAnnouncement = (a) => {
    let formattedExpiryForPicker = "";
    if (a.expiryDate) {
      formattedExpiryForPicker = a.expiryDate.replace(" ", "T").slice(0, 16);
    }
    setEditAnnouncementForm({
      id: a.id,
      title: a.title,
      body: a.body,
      audienceType: a.audienceType,
      targetFundId: a.audienceType === "fund" ? a.targetFundId : "",
      expiryDate: formattedExpiryForPicker,
      status: a.status,
      originalStatus: a.status,
      numberby: a.numberby,
    });
    setEditAnnouncementDialog(true);
  };

  const handleUpdateAnnouncement = async () => {
    const {
      id,
      title,
      body,
      audienceType,
      targetFundId,
      expiryDate,
      status,
      originalStatus,
      numberby,
    } = editAnnouncementForm;

    if (!title.trim() || !body.trim()) {
      setSnackbar({ open: true, message: "Title and body are required.", severity: "warning" });
      return;
    }
    if (audienceType === "fund" && !targetFundId) {
      setSnackbar({ open: true, message: "Please select a target fund.", severity: "warning" });
      return;
    }

    setIsUpdatingAnnouncement(true);
    const formattedExpiry = formatExpiryDateTime(expiryDate);

    // Checks for status transitions
    const isReactivating = !originalStatus && status; // Inactive -> Active
    const isDeactivating = originalStatus && !status; // Active -> Inactive

    // 1. Calculate the initial effective position
    let effectiveNumberBy = Number(numberby);
    if (isReactivating) {
      // Move to end of active list
      effectiveNumberBy = activeAnnouncements.length + 1;
    } else if (isDeactivating) {
      // Push to bottom when deactivated
      effectiveNumberBy = 9999;
    }

    try {
      // 2. Call PortfolioUpdateAnnouncement
      const response = await PortfolioUpdateAnnouncement(
        "",
        id,
        audienceType === "fund" ? targetFundId : "0",
        title,
        body,
        agentID,
        formattedExpiry,
        status ? 1 : 0,
        effectiveNumberBy
      );

      const callStatus = response?.data?.header?.callStatus;
      if (callStatus === "1" || callStatus === 1) {

        // 3. Update target item in local state
        const updatedList = announcements.map((item) => {
          if (String(item.id) === String(id)) {
            return {
              ...item,
              status: status,
              numberby: isReactivating || isDeactivating ? effectiveNumberBy : effectiveNumberBy - 0.1,
            };
          }
          return { ...item, numberby: Number(item.numberby) };
        });

        // 4. Group into active and inactive items
        const activeItems = updatedList.filter((item) => item.status);
        const inactiveItems = updatedList.filter((item) => !item.status);

        // Sort active items ascending by numberby
        activeItems.sort((a, b) => Number(a.numberby) - Number(b.numberby));
        // Sort inactive items ascending by numberby or ID
        inactiveItems.sort((a, b) => Number(a.numberby) - Number(b.numberby));

        // 5. Build clean, contiguous sequential mapping:
        // Active items get 1, 2, 3, 4...
        // Inactive items continue after active items (e.g., 5, 6...)
        const sortDataMap = {};
        let currentRank = 1;

        activeItems.forEach((item) => {
          sortDataMap[String(item.id)] = String(currentRank++);
        });

        inactiveItems.forEach((item) => {
          sortDataMap[String(item.id)] = String(currentRank++);
        });

        // 6. Send contiguous sort payload to PortfolioSortAnnouncement API
        await PortfolioSortAnnouncement("", JSON.stringify(sortDataMap));

        // 7. Refresh full list to sync state from backend
        await fetchAnnouncements();
        setEditAnnouncementDialog(false);
        setSnackbar({
          open: true,
          message: response?.data?.header?.msg || "Announcement updated and reordered.",
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: response?.data?.header?.msg || "Failed to update.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Failed to update announcement:", error);
      setSnackbar({ open: true, message: "Something went wrong.", severity: "error" });
    } finally {
      setIsUpdatingAnnouncement(false);
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    setDeletingAnnouncementId(id);
    try {
      const response = await PortfolioDeleteAnnouncement("", id);
      const callStatus = response?.data?.header?.callStatus;

      if (callStatus === "1" || callStatus === 1) {
        await fetchAnnouncements();
        setSnackbar({
          open: true,
          message: response?.data?.header?.msg || "Announcement deleted.",
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: response?.data?.header?.msg || "Failed to delete announcement.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Failed to delete announcement:", error);
      setSnackbar({ open: true, message: "Something went wrong.", severity: "error" });
    } finally {
      setAlert((prev) => ({ ...prev, showAlert: false }));
      setDeletingAnnouncementId(null);
    }
  };

  const confirmDeleteAnnouncement = (id, title) => {
    setAlert({
      showAlert: true,
      message: `Are you sure you want to delete "${title}"? This cannot be undone.`,
      severity: "confirm",
      header: "Delete Announcement",
      alertType: "confirm",
      onConfirm: () => handleDeleteAnnouncement(id),
    });
  };

  const activeAnnouncements = announcements.filter((a) => a.status);
  const inactiveAnnouncements = announcements.filter((a) => !a.status);

  const renderAnnouncementList = (list, page, rowsPerPage, setPage, setRowsPerPage) => {
    if (list.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 3 }}>
          No announcements found.
        </Typography>
      );
    }

    const paginatedItems = list.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
      <Box>
        {paginatedItems.map((a) => (
          <Box
            key={a.id}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 1.5,
              mb: 1,
              border: "1px solid #eee",
              borderRadius: 1,
              bgcolor: "#fff",
              opacity: deletingAnnouncementId === a.id ? 0.5 : 1,
            }}
          >
            <Box sx={{ flex: 1, mr: 2 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body1" fontWeight="bold">
                  {a.title}
                </Typography>
                <Typography variant="caption" sx={{ bgcolor: "#f0f0f0", px: 1, py: 0.2, borderRadius: 1 }}>
                  Order #: {a.numberby}
                </Typography>
              </Box>

              <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                {a.body}
              </Typography>

              <Box display="flex" gap={2} mt={0.5}>
                <Typography variant="caption" color="primary" fontWeight="500">
                  Target: {a.targetFundName}
                </Typography>
                {a.expiryDate && (
                  <Typography variant="caption" color="error">
                    Expires: {a.expiryDate}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  sx={{ color: a.status ? "green" : "gray", fontWeight: "bold" }}
                >
                  Status: {a.status ? "Active" : "Inactive"}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
              <IconButton
                size="small"
                color="primary"
                disabled={deletingAnnouncementId === a.id}
                onClick={() => openEditAnnouncement(a)}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                disabled={deletingAnnouncementId === a.id}
                onClick={() => confirmDeleteAnnouncement(a.id, a.title)}
              >
                {deletingAnnouncementId === a.id ? (
                  <CircularProgress size={16} color="error" />
                ) : (
                  <DeleteOutlineIcon fontSize="small" />
                )}
              </IconButton>
            </Box>
          </Box>
        ))}

        <TablePagination
          component="div"
          count={list.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Box>
    );
  };

  return (
    <Box className="dashboard-card" sx={{ mb: 3 }}>
      <Typography variant="h6" fontWeight="bold">
        New Announcement
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Published announcements appear on the investor dashboard.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Title"
            value={announcementForm.title}
            onChange={(e) => setAnnouncementForm((p) => ({ ...p, title: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Body"
            multiline
            minRows={3}
            value={announcementForm.body}
            onChange={(e) => setAnnouncementForm((p) => ({ ...p, body: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Audience</InputLabel>
            <Select
              label="Audience"
              value={announcementForm.audienceType}
              onChange={(e) =>
                setAnnouncementForm((p) => ({
                  ...p,
                  audienceType: e.target.value,
                  targetFundId: "",
                }))
              }
            >
              <MenuItem value="all">All Investors</MenuItem>
              <MenuItem value="fund">Specific Fund</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {announcementForm.audienceType === "fund" && (
          <Grid item xs={12} md={5}>
            <FormControl fullWidth size="small">
              <InputLabel>Target Fund</InputLabel>
              <Select
                label="Target Fund"
                value={announcementForm.targetFundId}
                onChange={(e) =>
                  setAnnouncementForm((p) => ({
                    ...p,
                    targetFundId: e.target.value,
                  }))
                }
              >
                {funds.map((f) => (
                  <MenuItem key={f.id} value={f.id}>
                    {f.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            label="Expiry Date & Time"
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
            value={announcementForm.expiryDate}
            onChange={(e) => setAnnouncementForm((p) => ({ ...p, expiryDate: e.target.value }))}
          />
        </Grid>

        <Grid item xs={12} md={4} display="flex" alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={announcementForm.status}
                onChange={(e) => setAnnouncementForm((p) => ({ ...p, status: e.target.checked }))}
                color="primary"
              />
            }
            label={announcementForm.status ? "Active Status" : "Inactive Status"}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1, borderStyle: "dashed" }} />
          <Button
            variant="contained"
            onClick={handleAnnouncementSubmit}
            disabled={isPublishingAnnouncement}
            startIcon={
              isPublishingAnnouncement ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={{ bgcolor: "#001529", textTransform: "none" }}
          >
            {isPublishingAnnouncement ? "Publishing..." : "Publish Announcement"}
          </Button>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)}>
          <Tab label={`Active Announcements (${activeAnnouncements.length})`} sx={{ textTransform: "none" }} />
          <Tab label={`Inactive Announcements (${inactiveAnnouncements.length})`} sx={{ textTransform: "none" }} />
        </Tabs>
      </Box>

      {isLoadingAnnouncements ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : activeTab === 0 ? (
        renderAnnouncementList(
          activeAnnouncements,
          pageActive,
          rowsPerPageActive,
          setPageActive,
          setRowsPerPageActive
        )
      ) : (
        renderAnnouncementList(
          inactiveAnnouncements,
          pageInactive,
          rowsPerPageInactive,
          setPageInactive,
          setRowsPerPageInactive
        )
      )}

      {/* Edit Dialog */}
      <Dialog
        open={editAnnouncementDialog}
        onClose={() => setEditAnnouncementDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Announcement</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Title"
                value={editAnnouncementForm.title}
                onChange={(e) =>
                  setEditAnnouncementForm((p) => ({ ...p, title: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Body"
                multiline
                minRows={3}
                value={editAnnouncementForm.body}
                onChange={(e) =>
                  setEditAnnouncementForm((p) => ({ ...p, body: e.target.value }))
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Display Order (numberby)"
                value={editAnnouncementForm.numberby}
                onChange={(e) =>
                  setEditAnnouncementForm((p) => ({
                    ...p,
                    numberby: e.target.value,
                  }))
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Audience</InputLabel>
                <Select
                  label="Audience"
                  value={editAnnouncementForm.audienceType}
                  onChange={(e) =>
                    setEditAnnouncementForm((p) => ({
                      ...p,
                      audienceType: e.target.value,
                      targetFundId: "",
                    }))
                  }
                >
                  <MenuItem value="all">All Investors</MenuItem>
                  <MenuItem value="fund">Specific Fund</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {editAnnouncementForm.audienceType === "fund" && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Target Fund</InputLabel>
                  <Select
                    label="Target Fund"
                    value={editAnnouncementForm.targetFundId}
                    onChange={(e) =>
                      setEditAnnouncementForm((p) => ({
                        ...p,
                        targetFundId: e.target.value,
                      }))
                    }
                  >
                    {funds.map((f) => (
                      <MenuItem key={f.id} value={f.id}>
                        {f.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Expiry Date & Time"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={editAnnouncementForm.expiryDate}
                onChange={(e) =>
                  setEditAnnouncementForm((p) => ({ ...p, expiryDate: e.target.value }))
                }
              />
            </Grid>

            <Grid item xs={12} md={6} display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={editAnnouncementForm.status}
                    onChange={(e) =>
                      setEditAnnouncementForm((p) => ({ ...p, status: e.target.checked }))
                    }
                    color="primary"
                  />
                }
                label={editAnnouncementForm.status ? "Active" : "Inactive"}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditAnnouncementDialog(false)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateAnnouncement}
            disabled={isUpdatingAnnouncement}
            startIcon={
              isUpdatingAnnouncement ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={{ bgcolor: "#001529", textTransform: "none" }}
          >
            {isUpdatingAnnouncement ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminAnnouncements;